import { QueryKeys } from "@/constants/Query";
import { useApi } from "@/hooks/useApi";
import axiosInstance from "@/lib/axios";
import { RequestGroup, ServiceRequest } from "@curai/js-fhir-tools";

interface LabsOrderParams {
  patientId: string;
  encounterId: string;
  enabled: boolean;
}

export enum LabOrderPaymentStatus {
  UNPAID = "unpaid",
  PAYMENT_REQUESTED = "payment_requested",
  PAID = "paid",
  NOT_REQUIRED = "not_required",
}

export interface LabOrderItem {
  encounterId: string;
  requestGroup: RequestGroup;
  tests: ServiceRequest[];
  paymentStatus: LabOrderPaymentStatus;
  readyForSubmission: boolean;
  submittedToLab: boolean;
  canCancel: boolean;
  canCancelWithoutPaymentCheck: boolean;
  canAddTests: boolean;
  aoeSatisfied: boolean;
}

interface LabsOrderResponse {
  labOrders: LabOrderItem[];
}

export const useLabOrder = ({
  patientId,
  encounterId,
  enabled,
}: LabsOrderParams) => {
  return useApi<LabsOrderResponse>({
    queryOptions: {
      enabled: Boolean(enabled && encounterId),
      queryFn: async () => {
        const response = await axiosInstance.get<LabsOrderResponse>(
          `/labs/order/encounter/${encounterId}`,
        );

        return response.data;
      },
      queryKey: QueryKeys.Patient.Chart.Labs.DEFAULT({
        patientId,
        encounterId,
      }),
      select: (data) => ({
        labOrders: data.labOrders.map((labOrder) => ({
          encounterId,
          requestGroup: new RequestGroup(labOrder.requestGroup),
          tests: labOrder.tests.map((test) => new ServiceRequest(test)),
          paymentStatus: labOrder.paymentStatus,
          readyForSubmission: labOrder.readyForSubmission,
          submittedToLab: labOrder.submittedToLab,
          canCancel: labOrder.canCancel,
          canCancelWithoutPaymentCheck: labOrder.canCancelWithoutPaymentCheck,
          canAddTests: labOrder.canAddTests,
          aoeSatisfied: labOrder.aoeSatisfied,
        })),
      }),
    },
  });
};
